import { useCallback, useEffect, useMemo, useState } from "react";
import { useProjects } from "@contexts/ProjectContext";
import { useAuth } from "@contexts/AuthContext";
import type { Project } from "@shared/types";
import {
  rejectedObservationRepository,
  type RejectedObservationDoc,
} from "@data/firestore/RejectedObservationRepository";
import { observationFingerprint } from "@shared/utils/observationFingerprint";
import type { AIObservation } from "@data/functions/projectReviewer";

interface ProjectContextValue {
  activeProject: Project | null | undefined;
}

export interface UseRejectedObservationsResult {
  /** All rejected observations for the active project. */
  items: RejectedObservationDoc[];
  /** Fast membership check by fingerprint. */
  isRejected(fingerprint: string): boolean;
  /** Convenience: check if this observation has a rejected twin. */
  isObservationRejected(obs: { title: string }): boolean;
  reject(obs: AIObservation, reason?: string): Promise<void>;
  undoReject(fingerprint: string): Promise<void>;
  loaded: boolean;
}

export function useRejectedObservations(): UseRejectedObservationsResult {
  const projects = useProjects() as ProjectContextValue;
  const activeProject = projects.activeProject;
  const { user } = useAuth();
  const [items, setItems] = useState<RejectedObservationDoc[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!activeProject) {
      setItems([]);
      setLoaded(false);
      return undefined;
    }
    const unsub = rejectedObservationRepository.watch(activeProject, (next) => {
      setItems(next);
      setLoaded(true);
    });
    return unsub;
  }, [activeProject]);

  const fingerprintSet = useMemo(
    () => new Set(items.map((i) => i.id)),
    [items]
  );

  const isRejected = useCallback(
    (fingerprint: string) => fingerprintSet.has(fingerprint),
    [fingerprintSet]
  );

  const isObservationRejected = useCallback(
    (obs: { title: string }) => fingerprintSet.has(observationFingerprint(obs.title)),
    [fingerprintSet]
  );

  const reject = useCallback(
    async (obs: AIObservation, reason?: string) => {
      if (!activeProject || !user) return;
      const fp = observationFingerprint(obs.title);
      const data: RejectedObservationDoc = {
        id: fp,
        title: obs.title,
        body: obs.body,
        category: obs.category,
        rejectedAt: Date.now(),
        rejectedBy: user.uid,
        ...(reason ? { reason } : {}),
      };
      await rejectedObservationRepository.add(activeProject, data);
    },
    [activeProject, user]
  );

  const undoReject = useCallback(
    async (fingerprint: string) => {
      if (!activeProject) return;
      await rejectedObservationRepository.remove(activeProject, fingerprint);
    },
    [activeProject]
  );

  return { items, isRejected, isObservationRejected, reject, undoReject, loaded };
}
